import { MOCK_DATA_MODEL_FIELDS_RESPONSE, MOCK_MODEL_RELEASE_ID } from "../chartStudio/chartStudioDataModel.mock";
import { mockNotFoundError, mockValidationError } from "../mockDomainError";
import { mockFetchCanvasItem } from "./storyCanvasApi.mock";
import { assertStoryExists, listMockStoryFilterRows } from "./storyFilters.mock";
import type {
  FieldValuesParams,
  FieldValuesResponseApi,
  FilterScalarApi,
  StoryFilterApi,
} from "./storyFilters.types";

/**
 * Mock `/models/{rel}/fields/{id}/values`. The paging, search and 404/422 rules
 * are the backend's, written here so the value menu can run offline. A field's
 * values are narrowed by the story's story-wide filters (and a chart's own,
 * when `canvas_item_id` is sent) on a *different* field of the same hierarchy:
 * Region = West leaves only West's states and cities. A field's own filter
 * never narrows its own list, so its editor still offers every value.
 */

const LATENCY_MS = 150;
const DEFAULT_LIMIT = 50;
const MAX_LIMIT = 200;

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const STATE_REGION: Record<string, string> = {
  California: "West",
  Washington: "West",
  Oregon: "West",
  Texas: "Southeast",
  Florida: "Southeast",
  Georgia: "Southeast",
  "New York": "Northeast",
  Pennsylvania: "Northeast",
  Illinois: "Midwest",
  Ohio: "Midwest",
};

const CITY_STATE: Record<string, string> = {
  "Los Angeles": "California",
  "San Francisco": "California",
  Seattle: "Washington",
  Portland: "Oregon",
  Houston: "Texas",
  Dallas: "Texas",
  Miami: "Florida",
  Atlanta: "Georgia",
  "New York City": "New York",
  Philadelphia: "Pennsylvania",
  Chicago: "Illinois",
  Columbus: "Ohio",
};

/** Distinct values per categorical or temporal field; measures have none and report a range instead. */
const VALUES_BY_FIELD: Record<string, string[]> = {
  order_date: ["2016-11-01", "2016-12-01", "2017-01-01", "2017-02-01", "2017-03-01"],
  order_time: ["09:00", "10:30", "13:15", "17:45"],
  /** The regions the seeded chart rows carry, so a story filter on Region visibly narrows them. */
  region: ["Midwest", "Northeast", "Southeast", "West"],
  store: ["Airport Kiosk", "Downtown Flagship", "Mall Outlet", "Uptown"],
  category: ["Furniture", "Office Supplies", "Technology"],
  product: ["Bookcase", "Chair", "Desk", "Monitor", "Paper", "Phone", "Stapler"],
  segment: ["Consumer", "Corporate", "Home Office"],
  channel: ["Online", "Partner", "Retail", "Wholesale"],
  state: Object.keys(STATE_REGION),
  city: Object.keys(CITY_STATE),
  order_id: Array.from({ length: 120 }, (_, i) => `ORD-${String(1001 + i)}`),
};

const RANGE_BY_FIELD: Record<string, { min: number; max: number }> = {
  sales: { min: 0.44, max: 22638.48 },
  quantity: { min: 1, max: 14 },
  profit: { min: -6599.98, max: 8399.98 },
  discount: { min: 0, max: 0.8 },
  margin_pct: { min: -2.75, max: 0.5 },
  revenue_per_unit: { min: 0.22, max: 1799.99 },
};

/** A temporal field's whole data range, sent with every page of its values whatever the search. */
const DATE_RANGE_BY_FIELD: Record<string, { min: string; max: string }> = {
  order_date: { min: "2016-11-01", max: "2017-03-01" },
};

const CATALOG_IDS = new Set(
  Object.values(MOCK_DATA_MODEL_FIELDS_RESPONSE).flatMap((fields) => fields.map((field) => field.id)),
);

/** The hierarchy a field sits in and how a value of it maps up to a region. */
const REGION_OF: Record<string, (value: string) => string | undefined> = {
  region: (value) => value,
  state: (value) => STATE_REGION[value],
  city: (value) => STATE_REGION[CITY_STATE[value]],
};

const asStrings = (value: StoryFilterApi["value"]): string[] =>
  (Array.isArray(value) ? value : [value]).map((scalar) => String(scalar));

/**
 * Regions a filter leaves in play, or null when it can't narrow this field.
 * Only an `in` / `eq` pick narrows; `not_in` / `neq` remove the listed regions.
 */
const regionsKept = (filter: StoryFilterApi): string[] | null => {
  const toRegion = REGION_OF[filter.field.model_field_id];
  if (!toRegion) return null;
  const picked = new Set(asStrings(filter.value).map(toRegion));
  const all = VALUES_BY_FIELD.region;
  if (filter.comparator === "in" || filter.comparator === "eq") return all.filter((r) => picked.has(r));
  if (filter.comparator === "not_in" || filter.comparator === "neq") return all.filter((r) => !picked.has(r));
  return null;
};

const narrowingFilters = (fieldId: string, params: FieldValuesParams): StoryFilterApi[] => {
  if (!params.storyId) return [];
  return listMockStoryFilterRows().filter(
    (filter) =>
      filter.field.model_field_id !== fieldId &&
      (filter.kind === "global" ||
        filter.kind === "cross" ||
        (!!params.canvasItemId &&
          filter.canvas_item_id === params.canvasItemId &&
          (filter.kind === "local" || filter.kind === "drill_down"))),
  );
};

const narrow = (fieldId: string, values: string[], params: FieldValuesParams): string[] => {
  const toRegion = REGION_OF[fieldId];
  if (!toRegion) return values;
  const allowed = narrowingFilters(fieldId, params)
    .map(regionsKept)
    .filter((regions): regions is string[] => regions !== null);
  return values.filter((value) => allowed.every((regions) => regions.includes(toRegion(value) ?? "")));
};

const validate = ({ limit, offset, canvasItemId, storyId }: FieldValuesParams): void => {
  if (limit != null && (!Number.isInteger(limit) || limit < 1 || limit > MAX_LIMIT)) {
    throw mockValidationError(`limit must be between 1 and ${MAX_LIMIT}`);
  }
  if (offset != null && (!Number.isInteger(offset) || offset < 0)) {
    throw mockValidationError("offset must be 0 or more");
  }
  if (canvasItemId && !storyId) throw mockValidationError("canvas_item_id needs story_id");
};

export async function mockFetchFieldValues(
  modelReleaseId: string,
  fieldId: string,
  params: FieldValuesParams = {},
): Promise<FieldValuesResponseApi> {
  await wait(LATENCY_MS);
  if (modelReleaseId !== MOCK_MODEL_RELEASE_ID) {
    throw mockNotFoundError(`Model release not found: ${modelReleaseId}`);
  }
  if (!CATALOG_IDS.has(fieldId) && !(fieldId in VALUES_BY_FIELD)) {
    throw mockNotFoundError(`Field not found: ${fieldId}`);
  }
  validate(params);
  if (params.storyId) assertStoryExists(params.storyId);
  if (params.storyId && params.canvasItemId) await mockFetchCanvasItem(params.storyId, params.canvasItemId);

  const range = RANGE_BY_FIELD[fieldId];
  if (range) return { values: [], has_more: false, ...range };

  const needle = params.search?.trim().toLowerCase();
  const matching = narrow(fieldId, VALUES_BY_FIELD[fieldId] ?? [], params)
    .filter((value) => !needle || value.toLowerCase().includes(needle))
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));

  const offset = params.offset ?? 0;
  const limit = params.limit ?? DEFAULT_LIMIT;
  const page: FilterScalarApi[] = matching.slice(offset, offset + limit);
  return {
    values: page,
    has_more: offset + page.length < matching.length,
    ...(DATE_RANGE_BY_FIELD[fieldId] ?? { min: null, max: null }),
  };
}
